import { useEffect, useState } from 'react';
import { fmtMonthDay, type SettingsDTO } from '@wd/shared';
import { errorText } from '../../lib/api';
import { useSettingsActions } from '../../lib/queries';
import { Banner, Button, Kbds } from '../../ui/primitives';
import { Group, Head, Row, SettingChoice, SettingToggle, rangeOptions, timeOptions, useSetting } from './parts';

// 设置的各个分区：通用、日报与异常（设计稿 14c）、AI 追问、可见范围、通知、快捷键。

export function ReadOnlyNote({ dto }: { dto: SettingsDTO }) {
  if (dto.canEdit) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <Banner icon="lock">只有管理员能修改这些设置。管理员：{dto.meta.admins.map((a) => a.name).join('、') || '暂无'}</Banner>
    </div>
  );
}

export function PendingBanner({ dto }: { dto: SettingsDTO }) {
  const { discard } = useSettingsActions();
  const n = Object.keys(dto.pending).length;
  if (!n || !dto.pendingFrom) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <Banner
        icon="clock"
        right={
          dto.canEdit ? (
            <Button variant="text" disabled={discard.isPending} onClick={() => discard.mutate()}>
              撤销
            </Button>
          ) : undefined
        }
      >
        有 {n} 项修改会在 {fmtMonthDay(dto.pendingFrom)} 起生效，今天仍按原来的规则提醒和判定。
      </Banner>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 通用
// ---------------------------------------------------------------------------

export function GeneralSection({ dto }: { dto: SettingsDTO }) {
  const { rename } = useSettingsActions();
  const [name, setName] = useState(dto.workspace.name);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setName(dto.workspace.name), [dto.workspace.name]);
  const save = () => {
    const n = name.trim();
    if (!n || n === dto.workspace.name) return setName(dto.workspace.name);
    rename.mutate(n, { onError: (e) => setError(errorText(e)), onSuccess: () => setError(null) });
  };
  return (
    <>
      <Head title="通用" desc="工作区的基本信息。" />
      <ReadOnlyNote dto={dto} />
      <Group title="工作区">
        <Row label="名称" desc={error ?? '显示在登录页、邀请邮件和周报里'}>
          {dto.canEdit ? (
            <input
              className="wd-input st-input"
              value={name}
              maxLength={30}
              onChange={(e) => setName(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
              aria-label="工作区名称"
            />
          ) : (
            <span className="st-ro">{dto.workspace.name}</span>
          )}
        </Row>
        <Row label="时区" desc="所有日期、提醒时间都按这个时区计算">
          <span className="st-ro">北京时间（UTC+8）</span>
        </Row>
        <Row label="管理员" desc="能修改设置、管理成员。在「团队与成员」里调整角色">
          <span className="st-ro">{dto.meta.admins.map((a) => a.name).join('、') || '暂无'}</span>
        </Row>
        {dto.workspace.createdAt && (
          <Row label="创建于">
            <span className="st-ro">{fmtMonthDay(dto.workspace.createdAt.slice(0, 10))}</span>
          </Row>
        )}
      </Group>
      <Group title="登录方式">
        <Row label="个人微信扫码" desc="认证服务号的带参数二维码；第一次扫码用工作邮箱绑定">
          <span className="st-ro">{dto.meta.wechatConfigured ? '已接入' : '未配置服务号'}</span>
        </Row>
        <Row label="工作邮箱验证码" desc="微信不方便时的备用方式">
          <span className="st-ro">{dto.meta.smtpConfigured ? '已接入' : '未配置邮件服务'}</span>
        </Row>
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// 日报与异常（设计稿 14c）
// ---------------------------------------------------------------------------

export function RulesSection({ dto, onHolidays }: { dto: SettingsDTO; onHolidays: () => void }) {
  const late = useSetting(dto, 'lateReminder');
  const t = (k: Parameters<typeof useSetting>[1]) => (k in dto.pending ? '明天起生效' : undefined);
  return (
    <>
      <Head title="日报与异常" desc="这些规则决定员工何时被提醒，以及哪些情况会进入主管的收件箱。修改后从明天起生效。" />
      <ReadOnlyNote dto={dto} />
      <PendingBanner dto={dto} />
      <Group title="日报">
        <Row label="每日截止时间" desc={`截止后未提交会在 ${late.value} 再提醒一次`} tag={t('deadline')}>
          <SettingChoice dto={dto} k="deadline" options={timeOptions('16:00', '21:00')} width={120} />
        </Row>
        <Row label="预填提醒" desc="到点推送微信提醒，内容已按昨日计划预填" tag={t('prefillReminder')}>
          <SettingChoice dto={dto} k="prefillReminder" options={timeOptions('14:00', '19:00')} width={120} />
        </Row>
        <Row label="逾期提醒" desc="截止后还没提交的再提醒一次，之后不再打扰" tag={t('lateReminder')}>
          <SettingChoice dto={dto} k="lateReminder" options={timeOptions('18:00', '23:00')} width={120} />
        </Row>
        <Row
          label="工作日"
          desc={
            <>
              法定节假日、调休和已登记的请假日不提醒、不预填 ·{' '}
              <button type="button" className="st-link" onClick={onHolidays}>
                节假日表
              </button>
            </>
          }
          tag={t('followHolidays')}
        >
          <SettingChoice
            dto={dto}
            k="followHolidays"
            options={[
              { value: true, label: '跟随法定节假日' },
              { value: false, label: '仅周一至周五' },
            ]}
          />
        </Row>
      </Group>
      <Group title="异常判定" right={<span>每天 {dto.current.rulesAt} 生成</span>}>
        <Row label="阻塞超时" desc="同一阻塞连续出现在日报中" tag={t('blockedTimeoutDays')}>
          <SettingChoice dto={dto} k="blockedTimeoutDays" options={rangeOptions(1, 10, (n) => `${n} 个工作日`)} width={140} />
        </Row>
        <Row label="日期反复延期" desc="截止日被后延的次数（同一项目）" tag={t('dueSlipCount')}>
          <SettingChoice dto={dto} k="dueSlipCount" options={rangeOptions(1, 6, (n) => `${n} 次`)} width={120} />
        </Row>
        <Row label="项目停滞" desc="没有任何日报提到进展；请假期间不计算" tag={t('stalledDays')}>
          <SettingChoice dto={dto} k="stalledDays" options={rangeOptions(3, 15, (n) => `${n} 个工作日`)} width={140} />
        </Row>
        <Row label="说了没做" desc="勾选的明日计划未执行且未说明原因" tag={t('saidNotDoneStreak')}>
          <SettingChoice dto={dto} k="saidNotDoneStreak" options={rangeOptions(1, 5, (n) => `连续 ${n} 次`)} width={120} />
        </Row>
        <Row label="每天最多进入收件箱" desc="超出时按影响排序，其余进入雷达" tag={t('inboxDailyMax')}>
          <SettingChoice dto={dto} k="inboxDailyMax" options={rangeOptions(3, 10, (n) => `${n} 条`)} width={120} />
        </Row>
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// AI 追问
// ---------------------------------------------------------------------------

const WEEKDAYS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

export function AISection({ dto }: { dto: SettingsDTO }) {
  const m = dto.meta;
  return (
    <>
      <Head
        title="AI 追问"
        desc="AI 只读日报里的文字：按昨日计划预填、追问含糊的地方、起草周报。发出前人名和客户名会换成代号；AI 只谈项目，不评价个人表现，所有决定都由人来做。"
      />
      <ReadOnlyNote dto={dto} />
      <Group title="追问">
        <Row label="提交前追问" desc="日报里有说不清的地方（例如“在等回复”却没说等什么）时，提交前追问一句">
          <SettingChoice
            dto={dto}
            k="aiFollowUpsPerReport"
            options={[
              { value: 0, label: '关闭' },
              { value: 1, label: '每份最多 1 次' },
              { value: 2, label: '每份最多 2 次' },
            ]}
            width={160}
          />
        </Row>
      </Group>
      <Group title="周报">
        <Row label="自动起草" desc="到点根据本周日报起草周报，并提醒主管核对后发送">
          <SettingToggle dto={dto} k="weeklyDraftEnabled" label="自动起草周报" />
        </Row>
        {dto.current.weeklyDraftEnabled && (
          <Row label="起草时间" desc="起草时只读当周已提交的日报">
            <span className="row" style={{ gap: 6 }}>
              <SettingChoice dto={dto} k="weeklyDraftWeekday" options={WEEKDAYS.map((w, i) => ({ value: i + 1, label: w }))} width={100} />
              <SettingChoice dto={dto} k="weeklyDraftAt" options={timeOptions('09:00', '21:00', 60)} width={100} />
            </span>
          </Row>
        )}
      </Group>
      <Group title="运行状态">
        <Row label="状态" desc={m.aiEnabled ? '预填、追问、周报都由 AI 完成；AI 出错时自动退回规则模板' : '还没配置 AI，预填、追问、周报都按规则模板生成'}>
          <span className="st-ro">{m.aiEnabled ? '已接入' : '未配置'}</span>
        </Row>
        {m.aiEnabled && (
          <>
            <Row label="模型">
              <span className="st-ro">{m.aiModel}</span>
            </Row>
            {m.aiProvider === 'deepseek' ? (
              <Row label="调用方式" desc="国内服务器直连 DeepSeek，数据不出境；发给模型前人名、客户名都换成代号">
                <span className="st-ro">DeepSeek · 国内直连</span>
              </Row>
            ) : (
              <Row label="调用方式" desc="国内服务器经海外节点转发，节点不保存内容">
                <span className="st-ro">{m.aiRelay ? `经 ${m.aiRelay}` : '直连'}</span>
              </Row>
            )}
          </>
        )}
        <Row label="近 24 小时" desc="失败的调用会自动改用规则模板，员工不受影响">
          <span className="st-ro">
            {m.aiCallsToday.ok} 次成功{m.aiCallsToday.failed ? ` · ${m.aiCallsToday.failed} 次失败` : ''}
          </span>
        </Row>
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// 可见范围
// ---------------------------------------------------------------------------

const VISIBILITY: [string, string][] = [
  ['完整日报', '本人、直属主管、本团队的主管、管理员。包括明日计划和补充说明'],
  ['草稿与修改记录', '只有本人。撤回修改后，主管引用的地方会标“原文已修改”'],
  ['收件箱异常', '项目负责人的直属主管；被指派的人只看到指派给自己的那一条'],
  ['周报', '主管和管理员。只汇总项目，不点评个人'],
  ['请假与休息', '本人、直属主管；项目成员只看到“负责人休假中”'],
  ['操作日志、导出', '只有管理员'],
];

export function VisibilitySection({ dto }: { dto: SettingsDTO }) {
  return (
    <>
      <Head title="可见范围" desc="谁能看到什么。员工在「我 › 谁能看到什么」里看到的是与自己相关的部分。" />
      <ReadOnlyNote dto={dto} />
      <Group title="内容">
        {VISIBILITY.map(([k, v]) => (
          <Row key={k} label={k} desc={v} />
        ))}
      </Group>
      <Group title="项目">
        <Row
          label="项目动态可见范围"
          desc={
            dto.current.projectVisibility === 'all'
              ? '工作区里所有人都能看每个项目的进展、讨论和健康信号'
              : '项目负责人和参与人、他们的主管（直属主管、本团队主管、负责人的各级上级）、管理员。其他人只看得到项目名称、负责人和状态，问 AI 也不会用到这些项目'
          }
        >
          <SettingChoice
            dto={dto}
            k="projectVisibility"
            options={[
              { value: 'related', label: '相关的人和主管' },
              { value: 'all', label: '所有人' },
            ]}
            width={160}
          />
        </Row>
      </Group>
      <Group title="建议关注">
        <Row label="名单可见范围" desc="团队状态里的“值得认可 / 建议沟通”只给主管看，员工本人和同事看不到；不会出现在周报里。主管的沟通备注只有写的人能看到">
          <SettingChoice
            dto={dto}
            k="insightVisibility"
            options={[
              { value: 'direct', label: '仅直属主管' },
              { value: 'chain', label: '直属主管和上级' },
            ]}
            width={160}
          />
        </Row>
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// 通知
// ---------------------------------------------------------------------------

export function NotifySection({ dto }: { dto: SettingsDTO }) {
  return (
    <>
      <Head title="通知" desc="通知优先发到绑定的个人微信（服务号模板消息）；没绑定的人在网页收件箱里看到。" />
      <ReadOnlyNote dto={dto} />
      <Group title="提醒员工">
        <Row label="预填提醒" desc={`每天 ${dto.current.prefillReminder} 提醒写日报，内容已预填`}>
          <SettingToggle dto={dto} k="notifyPrefill" label="预填提醒" />
        </Row>
        <Row label="逾期提醒" desc={`${dto.current.lateReminder} 还没提交的再提醒一次`}>
          <SettingToggle dto={dto} k="notifyLate" label="逾期提醒" />
        </Row>
        <Row label="主管追问、指派、提到我" desc="即时推送，不能关闭：这些都需要本人回应">
          <span className="st-ro">始终开启</span>
        </Row>
      </Group>
      <Group title="提醒主管">
        <Row label="周报" desc="草稿起草好、以及有人发送周报时通知">
          <SettingToggle dto={dto} k="notifyWeekly" label="周报通知" />
        </Row>
        <Row label="收件箱异常" desc={`每天 ${dto.current.rulesAt} 生成后，在收件箱里查看；不单独推送`}>
          <span className="st-ro">收件箱</span>
        </Row>
      </Group>
      <Group title="渠道">
        <Row label="个人微信（服务号）" desc="员工第一次扫码登录时绑定">
          <span className="st-ro">{dto.meta.wechatConfigured ? '已接入' : '未配置'}</span>
        </Row>
        <Row label="邮件" desc="登录验证码、成员邀请">
          <span className="st-ro">{dto.meta.smtpConfigured ? '已接入' : '未配置'}</span>
        </Row>
      </Group>
    </>
  );
}

// ---------------------------------------------------------------------------
// 快捷键
// ---------------------------------------------------------------------------

const SHORTCUTS: [string, string[][]][] = [
  ['全局', [['⌘', 'K'], ['G', 'I'], ['G', 'D'], ['G', 'R'], ['G', 'T'], ['G', 'W'], ['G', 'S'], ['C']]],
  ['列表（收件箱、雷达）', [['J'], ['K'], ['↵'], ['F'], ['⌘', 'B']]],
  ['日报', [['E'], ['P'], ['1'], ['⌘', '↵']]],
  ['周报', [['←'], ['→'], ['⌘', '↵'], ['⌘', 'C']]],
];
const SHORTCUT_LABEL: Record<string, string> = {
  '⌘K': '搜索或跳转',
  GI: '打开收件箱',
  GD: '打开我的日报',
  GR: '打开项目雷达',
  GT: '打开团队状态（主管）',
  GW: '打开周报（主管）',
  GS: '打开设置',
  C: '写今天的日报',
  J: '下一条',
  K: '上一条',
  '↵': '打开',
  F: '筛选',
  '⌘B': '切换列表 / 看板',
  E: '编辑选中的进展',
  P: '添加明日计划',
  '1': '选择追问的推荐回答',
  '⌘↵': '提交 / 发送',
  '←': '上一周',
  '→': '下一周',
  '⌘C': '复制为文本',
};

export function ShortcutsSection() {
  return (
    <>
      <Head title="快捷键" desc="只在电脑上可用。输入框里字母键不触发，⌘ 组合键仍然有效。" />
      {SHORTCUTS.map(([group, keys]) => (
        <Group key={group} title={group}>
          {keys.map((k) => (
            <Row key={k.join('')} label={SHORTCUT_LABEL[k.join('')] ?? k.join(' ')}>
              <Kbds keys={k} />
            </Row>
          ))}
        </Group>
      ))}
    </>
  );
}
